import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Uppy from '@uppy/core';
import { message } from 'antd';
import { fabric } from 'fabric';
import { useProxyGrant } from './use-proxy-grant';
import {
  ScreenCaptureController,
  useScreenCapture
} from './use-screen-capture';
import { isCurrentTabScreenCaptureSupported } from './screen-capture-coordinator';
import { WEBSITE_SNAPSHOT_ATTACHMENT_NAME } from '@config/constants';
import {
  ProxyBridge,
  type ProxyParentBridge,
  type ProxyParentToChildMessage
} from '@website-proxy/frameBridge';
import type { AssetVersionItemDto } from '@api/Api';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { FabricContext } from '@context/FabricContext';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas';
import {
  WEBSITE_COORDINATE_TRANSITION_FLAG,
  WEBSITE_HISTORY_REBASE_EVENT
} from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useCanvasHistory';
import { nonNull } from '@helpers/non-null';
import { useTypedSelector } from '@hooks';
import useHasAnnotations from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useHasAnnotations';
import { useResponsive } from '@hooks/useResponsive';
import { isDrawToolInUse } from '@pages/MediaViewer/helpers/drawToolShape';
import { useUppyState } from '@hooks/useUppyState';

type HistoryEntry = { url: string; state: unknown; docId: number };

type HistoryState = {
  entries: HistoryEntry[];
  idx: number;
  currentDocId: number;
  nextDocId: number;
};

type SnapshotState =
  | {
      kind: 'object-url';
      url: string;
      coord: { width: number; height: number };
    }
  | {
      kind: 'attachment-url';
      url: string;
      coord: { width: number; height: number };
    };

export type Viewport = 'desktop' | 'tablet' | 'mobile';

const VIEWPORT_SIZES: Record<Viewport, number> = {
  desktop: 1024,
  tablet: 768,
  mobile: 390
};

type ViewportAvailability = Record<Viewport, boolean>;

type WebsiteAssetVersion = AssetVersionItemDto & {
  metadata: { type: 'website' };
};

interface UseWebsiteCardControllerResult {
  frameElement: React.ReactElement;
  faviconUrl: string | null;
  clearFavicon: () => void;
  currentUrl: string;
  viewport: Viewport;
  viewportAvailability: ViewportAvailability;
  setViewport: (viewport: Viewport) => void;
  canBack: boolean;
  canForward: boolean;
  isLoading: boolean;
  loadingButton: 'back' | 'forward' | 'reload' | null;
  back: () => void;
  forward: () => void;
  reload: () => void;
  hardReload: () => void;
  openInNewTab: () => void;
  copyUrl: () => void;
  snapshot: boolean;
  takingSnapshot: boolean;
  cancelSnapshot: () => void;
  isCaptureUnsupportedModalOpen: boolean;
  dismissCaptureUnsupportedModal: () => void;
}

interface UseWebsiteCardControllerOptions {
  assetVersion: WebsiteAssetVersion;
  uppy: Uppy;
  viewerType: 'main' | 'left' | 'right';
}

export function useWebsiteCardController({
  assetVersion,
  uppy,
  viewerType
}: UseWebsiteCardControllerOptions): UseWebsiteCardControllerResult {
  const { url, proxyUrl } = assetVersion.metadata;
  const frameRef = useRef<HTMLDivElement>(null);
  const mountRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const bridgeRef = useRef<ProxyParentBridge | null>(null);
  const canvasOverlayRef = useRef<HTMLDivElement | null>(null);

  const grant = useProxyGrant(assetVersion.id, proxyUrl);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState(url);
  const [pageTitle, setPageTitle] = useState<string>(assetVersion.name);
  const [error, setError] = useState<string | null>(null);
  const [viewport, _setViewport] = useState<Viewport>('desktop');
  const [availableWidth, setAvailableWidth] = useState<number | null>(null);
  const viewportAvailability = useMemo<ViewportAvailability>(
    () => ({
      desktop:
        availableWidth === null || availableWidth >= VIEWPORT_SIZES.desktop,
      tablet:
        availableWidth === null || availableWidth >= VIEWPORT_SIZES.tablet,
      mobile: true
    }),
    [availableWidth]
  );
  const [history, setHistoryState] = useState<HistoryState>({
    entries: [],
    idx: -1,
    currentDocId: 0,
    nextDocId: 1
  });
  const historyRef = useRef(history);
  const [loadingButton, setLoadingButton] = useState<
    'back' | 'forward' | 'reload' | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    shapesDrawingTools,
    commentsList,
    selectedCommentId,
    canvasMode,
    isMobileDrawingToolbarOpen,
    dismissAnnotation,
    setZoomFactor,
    setWebsitePageUrl
  } = useAssetViewerState();
  const { isMdWidth } = useResponsive();
  const isNewComment = ['new', 'reply'].includes(selectedCommentId ?? '');
  const isDrawingOnCanvas = isDrawToolInUse({
    shape: shapesDrawingTools.shape,
    isMdWidth,
    isMobileDrawingToolbarOpen,
    selectedCommentId
  });
  const isCanvasInteractive =
    canvasMode === 'edit' || isNewComment || isDrawingOnCanvas;
  const hasAnnotations = useHasAnnotations();
  const snapshotRequested = isNewComment && hasAnnotations;
  const uppyState = useUppyState(uppy);
  const draftSnapshotFile = useMemo(
    () =>
      Object.values(uppyState.files).find(
        (file) => file.name === WEBSITE_SNAPSHOT_ATTACHMENT_NAME
      ),
    [uppyState.files]
  );

  const startScreenCapture = useScreenCapture();
  const screenCaptureAbortController = useRef(new AbortController());
  const screenCapture = useRef<ScreenCaptureController | null>(null);
  const captureRegionOverrideRef = useRef<HTMLElement | null>(null);
  const screenCaptureSnapshotCompleted = useRef(false);
  const [draftSnapshot, setDraftSnapshot] = useState<SnapshotState | null>(
    null
  );
  const [attachmentSnapshot, setAttachmentSnapshot] =
    useState<SnapshotState | null>(null);
  const [takingSnapshot, setTakingSnapshot] = useState(false);
  const [isCaptureUnsupportedModalOpen, setIsCaptureUnsupportedModalOpen] =
    useState(false);
  const [loadingAttachmentSnapshotUrl, setLoadingAttachmentSnapshotUrl] =
    useState<string | null>(null);
  const fabricContext = useContext(FabricContext);
  const { clearCanvas, resetInteraction } = fabricContext;

  const selectedComment = useMemo(() => {
    if (!selectedCommentId) return null;
    if (selectedCommentId === 'new' || selectedCommentId === 'reply') {
      return null;
    }
    return commentsList.find((comment) => comment.id === selectedCommentId);
  }, [commentsList, selectedCommentId]);

  const selectedCommentSnapshotUrl = useMemo(() => {
    const snapshotAttachment = selectedComment?.attachments?.find((file) => {
      return file.name === WEBSITE_SNAPSHOT_ATTACHMENT_NAME && !!file.url;
    });
    return snapshotAttachment?.url ?? null;
  }, [selectedComment]);

  // While a comment (its snapshot) is viewed the cursor is the tool in use
  // (see AssetViewerStateProvider), the selected one is back once it's closed

  const websitePageUrl = selectedCommentSnapshotUrl
    ? selectedComment?.websitePageUrl ?? ''
    : currentUrl;

  useEffect(() => {
    const width = Number(draftSnapshotFile?.meta.websiteSnapshotWidth);
    const height = Number(draftSnapshotFile?.meta.websiteSnapshotHeight);
    const data = draftSnapshotFile?.data;
    if (
      !isNewComment ||
      !(data instanceof Blob) ||
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width <= 0 ||
      height <= 0
    ) {
      setDraftSnapshot(null);
      return;
    }

    const objectUrl = URL.createObjectURL(data);
    setDraftSnapshot({
      kind: 'object-url',
      url: objectUrl,
      coord: { width, height }
    });
    return () => URL.revokeObjectURL(objectUrl);
  }, [draftSnapshotFile, isNewComment]);

  const snapshot = isNewComment ? draftSnapshot : attachmentSnapshot;
  const isPersistedSnapshot = snapshot?.kind === 'attachment-url';

  useLayoutEffect(() => {
    const regionOverride = isPersistedSnapshot
      ? document.documentElement
      : null;
    captureRegionOverrideRef.current = regionOverride;
    screenCapture.current?.setRegionOverride(regionOverride);
  }, [isPersistedSnapshot]);

  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const _syncCanvasSize = () => {
    const { canvas } = fabricContext;
    const mount = mountRef.current;
    const overlay = canvasOverlayRef.current;
    if (!canvas || !mount || !overlay) return;

    const mountRect = mount.getBoundingClientRect();
    const currentSnapshot = snapshotRef.current;
    let left = 0;
    let top = 0;
    let { width } = mountRect;
    let { height } = mountRect;
    let scale = 1;

    if (currentSnapshot) {
      const { width: sourceWidth, height: sourceHeight } =
        currentSnapshot.coord;
      if (!sourceWidth || !sourceHeight || !width || !height) return;
      scale = Math.min(width / sourceWidth, height / sourceHeight);
      width = sourceWidth * scale;
      height = sourceHeight * scale;
      left = (mountRect.width - width) / 2;
      top = (mountRect.height - height) / 2;
    } else {
      const iframeRect = iframeRef.current?.getBoundingClientRect();
      if (iframeRect) {
        left = iframeRect.left - mountRect.left;
        top = iframeRect.top - mountRect.top;
        width = iframeRect.width;
        height = iframeRect.height;
      }
    }

    const roundedWidth = Math.max(1, Math.round(width));
    const roundedHeight = Math.max(1, Math.round(height));
    overlay.style.left = `${left}px`;
    overlay.style.top = `${top}px`;
    overlay.style.width = `${roundedWidth}px`;
    overlay.style.height = `${roundedHeight}px`;
    canvas.setDimensions({ width: roundedWidth, height: roundedHeight });
    canvas.setViewportTransform([10 * scale, 0, 0, 10 * scale, 0, 0]);
    canvas.calcOffset();
    canvas.getObjects().forEach((object) => object.setCoords());
    canvas.requestRenderAll();
    setZoomFactor(scale);
  };
  const syncCanvasSize = useRef(_syncCanvasSize);
  syncCanvasSize.current = _syncCanvasSize;

  const sendCommand = useRef((command: ProxyParentToChildMessage) => {
    bridgeRef.current?.send(command);
  });

  const updateHistory = useRef((next: HistoryState) => {
    const lengthChanged =
      next.entries.length !== historyRef.current.entries.length;
    historyRef.current = next;
    setHistoryState(next);
    if (lengthChanged) {
      sendCommand.current({
        type: 'stack-length',
        length: Math.max(1, next.entries.length)
      });
    }
  });

  const traverseTo = useRef(
    (newIdx: number, initiator: 'back' | 'forward' | null = null) => {
      const cur = historyRef.current;
      if (newIdx < 0 || newIdx >= cur.entries.length || newIdx === cur.idx) {
        return;
      }
      const target = cur.entries[newIdx];
      const sameDoc = target.docId === cur.currentDocId;
      setCurrentUrl(target.url);
      if (sameDoc) {
        sendCommand.current({
          type: 'traverse-same-doc',
          url: target.url,
          state: target.state
        });
        updateHistory.current({ ...cur, idx: newIdx });
      } else {
        if (initiator) setLoadingButton(initiator);
        setIsLoading(true);
        sendCommand.current({ type: 'load', url: target.url });
        updateHistory.current({
          ...cur,
          idx: newIdx,
          currentDocId: target.docId
        });
      }
    }
  );

  const canBack = history.idx > 0;
  const canForward = history.idx < history.entries.length - 1;

  const prevAssetName = useRef(assetVersion.name);
  if (prevAssetName.current !== assetVersion.name) {
    prevAssetName.current = assetVersion.name;
    setPageTitle(assetVersion.name);
  }

  const prevUrl = useRef(url);
  if (prevUrl.current !== url) {
    prevUrl.current = url;
    setCurrentUrl(url);
  }

  const prevProxyUrl = useRef(proxyUrl);
  if (prevProxyUrl.current !== proxyUrl) {
    prevProxyUrl.current = proxyUrl;
    historyRef.current = {
      entries: [],
      idx: -1,
      currentDocId: 0,
      nextDocId: 1
    };
    setHistoryState(historyRef.current);
    setFaviconUrl(null);
    setError(null);
    setLoadingButton(null);
    setIsLoading(true);
  }

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !grant.ready || !!error) {
      return;
    }

    let iframe = iframeRef.current;
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.setAttribute(
        'sandbox',
        'allow-scripts allow-same-origin allow-forms allow-popups-to-escape-sandbox allow-presentation'
      );
      mount.prepend(iframe);
      iframeRef.current = iframe;
    } else if (iframe.parentElement !== mount) {
      mount.prepend(iframe);
    }

    if (iframe.title !== pageTitle) {
      iframe.title = pageTitle;
    }

    if (viewport === 'desktop') {
      iframe.style.removeProperty('width');
    } else {
      const viewportWidth =
        viewport === 'mobile' && availableWidth !== null
          ? Math.min(VIEWPORT_SIZES.mobile, availableWidth)
          : VIEWPORT_SIZES[viewport];
      iframe.style.width = `${viewportWidth}px`;
    }

    if (iframe.getAttribute('src') !== proxyUrl) {
      iframe.src = proxyUrl;
      if (bridgeRef.current) {
        bridgeRef.current.destroy();
      }
      const bridge = ProxyBridge.forParent(
        () => iframeRef.current?.contentWindow ?? null
      );
      bridgeRef.current = bridge;
    }
  }, [availableWidth, grant.ready, error, pageTitle, proxyUrl, viewport]);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const resizeObserver = new ResizeObserver(([entry]) => {
      const nextWidth = entry.contentRect.width;
      if (nextWidth <= 0) return;
      setAvailableWidth((currentWidth) =>
        currentWidth === nextWidth ? currentWidth : nextWidth
      );
    });
    resizeObserver.observe(frame);
    return () => resizeObserver.disconnect();
  }, []);

  useLayoutEffect(() => {
    const mount = mountRef.current;
    const iframe = iframeRef.current;
    const canvas = fabricContext.canvas ?? null;
    if (!mount || !canvas) return;
    const resizeObserver = new ResizeObserver(syncCanvasSize.current);
    resizeObserver.observe(mount);
    if (iframe) resizeObserver.observe(iframe);
    syncCanvasSize.current();
    return () => {
      resizeObserver.disconnect();
    };
  }, [fabricContext.canvas, snapshot, grant.ready, viewport]);

  useEffect(() => {
    const overlay = canvasOverlayRef.current;
    if (
      !overlay ||
      !grant.ready ||
      !!error ||
      snapshot ||
      !isCanvasInteractive
    ) {
      return;
    }

    let pointerPosition = {
      clientX: overlay.clientWidth / 2,
      clientY: overlay.clientHeight / 2,
      screenX: 0,
      screenY: 0
    };

    const updatePointerPosition = (event: PointerEvent | WheelEvent) => {
      const iframe = iframeRef.current;
      if (!iframe) return;
      const iframeRect = iframe.getBoundingClientRect();
      pointerPosition = {
        clientX: event.clientX - iframeRect.left,
        clientY: event.clientY - iframeRect.top,
        screenX: event.screenX,
        screenY: event.screenY
      };
    };

    const forwardWheel = (event: WheelEvent) => {
      const iframe = iframeRef.current;
      const bridge = bridgeRef.current;
      if (!iframe || !bridge) return;

      const iframeRect = iframe.getBoundingClientRect();
      const clientX = event.clientX - iframeRect.left;
      const clientY = event.clientY - iframeRect.top;
      if (
        clientX < 0 ||
        clientY < 0 ||
        clientX >= iframeRect.width ||
        clientY >= iframeRect.height
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      updatePointerPosition(event);
      bridge.send({
        type: 'wheel',
        clientX,
        clientY,
        screenX: event.screenX,
        screenY: event.screenY,
        deltaX: event.deltaX,
        deltaY: event.deltaY,
        deltaZ: event.deltaZ,
        deltaMode: event.deltaMode,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        metaKey: event.metaKey
      });
    };

    const forwardArrowKey = (event: KeyboardEvent) => {
      const arrowDeltas: Partial<Record<string, [number, number]>> = {
        ArrowUp: [0, -3],
        ArrowDown: [0, 3],
        ArrowLeft: [-3, 0],
        ArrowRight: [3, 0]
      };
      const delta = arrowDeltas[event.key];
      const { activeElement } = document;
      const isEditing =
        activeElement instanceof HTMLElement &&
        (activeElement.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement.tagName) ||
          activeElement.getAttribute('role') === 'textbox');
      if (
        !delta ||
        event.defaultPrevented ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.metaKey ||
        isEditing ||
        !overlay.matches(':hover')
      ) {
        return;
      }

      const bridge = bridgeRef.current;
      if (!bridge) return;
      event.preventDefault();
      event.stopPropagation();
      bridge.send({
        type: 'wheel',
        ...pointerPosition,
        deltaX: delta[0],
        deltaY: delta[1],
        deltaZ: 0,
        deltaMode: WheelEvent.DOM_DELTA_LINE,
        ctrlKey: false,
        shiftKey: false,
        altKey: false,
        metaKey: false
      });
    };

    overlay.addEventListener('pointermove', updatePointerPosition);
    overlay.addEventListener('wheel', forwardWheel, {
      capture: true,
      passive: false
    });
    document.addEventListener('keydown', forwardArrowKey, { capture: true });
    return () => {
      overlay.removeEventListener('pointermove', updatePointerPosition);
      overlay.removeEventListener('wheel', forwardWheel, { capture: true });
      document.removeEventListener('keydown', forwardArrowKey, {
        capture: true
      });
    };
  }, [error, grant.ready, isCanvasInteractive, snapshot]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !grant.ready || !!error) {
      return;
    }
    const bridge = nonNull(bridgeRef.current);
    const unsubscribes = [
      bridge.subscribe('service-worker-not-supported', () => {
        setError('Service worker is not supported or failed to install.');
      }),
      bridge.subscribe('service-worker-install-failed', () => {
        setError('Service worker is not supported or failed to install.');
      }),
      bridge.subscribe('navigate', (data) => {
        setCurrentUrl(data.url);
        const cur = historyRef.current;
        let { currentDocId, nextDocId } = cur;
        let entryDocId: number;

        if (data.sameDoc) {
          entryDocId = currentDocId;
        } else {
          entryDocId = nextDocId;
          nextDocId += 1;
          currentDocId = entryDocId;
        }

        let newEntries: HistoryEntry[];
        let newIdx: number;
        if (data.navigationType === 'replace') {
          if (cur.entries.length === 0) {
            newEntries = [
              { url: data.url, state: data.state, docId: entryDocId }
            ];
            newIdx = 0;
          } else {
            newEntries = cur.entries.slice();
            newEntries[cur.idx] = {
              url: data.url,
              state: data.state,
              docId: entryDocId
            };
            newIdx = cur.idx;
          }
        } else {
          const trunc = cur.idx < 0 ? [] : cur.entries.slice(0, cur.idx + 1);
          trunc.push({
            url: data.url,
            state: data.state,
            docId: entryDocId
          });
          newEntries = trunc;
          newIdx = trunc.length - 1;
        }

        updateHistory.current({
          entries: newEntries,
          idx: newIdx,
          currentDocId,
          nextDocId
        });

        if (!data.sameDoc) {
          setIsLoading(true);
        }
        if (!data.sameDoc && !data.letThrough) {
          sendCommand.current({ type: 'load', url: data.url });
        }
      }),
      bridge.subscribe('loaded', (data) => {
        setCurrentUrl(data.url);
        setIsLoading(false);
        setLoadingButton(null);
        const cur = historyRef.current;
        if (cur.entries.length === 0) {
          const docId = cur.nextDocId;
          updateHistory.current({
            entries: [{ url: data.url, state: null, docId }],
            idx: 0,
            currentDocId: docId,
            nextDocId: docId + 1
          });
          return;
        }
        const entry = cur.entries[cur.idx];
        if (entry && entry.url !== data.url) {
          const next = cur.entries.slice();
          next[cur.idx] = { ...entry, url: data.url };
          updateHistory.current({ ...cur, entries: next });
        }
        if (entry && entry.state != null) {
          sendCommand.current({
            type: 'restore-state',
            state: entry.state
          });
        }
      }),
      bridge.subscribe('traverse-request', (data) => {
        const delta = Math.trunc(Number(data.delta) || 0);
        if (delta === 0) return;
        const cur = historyRef.current;
        const newIdx = Math.max(
          0,
          Math.min(cur.entries.length - 1, cur.idx + delta)
        );
        traverseTo.current(newIdx);
      }),
      bridge.subscribe('favicon', (data) => {
        setFaviconUrl(data.href);
      }),
      bridge.subscribe('title', (data) => {
        setPageTitle(data.title);
      }),
      bridge.subscribe('keydown', ({ type: _, ...data }) => {
        document.dispatchEvent(
          new KeyboardEvent('keydown', {
            ...data,
            view: window,
            bubbles: true,
            cancelable: true,
            composed: true
          })
        );
      })
    ];
    return () => {
      unsubscribes.forEach((fn) => fn());
    };
  }, [grant.ready, error, proxyUrl]);

  const back = useCallback(() => {
    if (!canBack) return;
    traverseTo.current(historyRef.current.idx - 1, 'back');
  }, [canBack]);

  const forward = useCallback(() => {
    if (!canForward) return;
    traverseTo.current(historyRef.current.idx + 1, 'forward');
  }, [canForward]);

  const reload = useCallback(() => {
    setLoadingButton('reload');
    setIsLoading(true);
    sendCommand.current({ type: 'reload' });
  }, []);

  const hardReload = useCallback(() => {
    setLoadingButton('reload');
    setIsLoading(true);
    sendCommand.current({ type: 'clear-site-data' });
  }, []);

  const openInNewTab = useCallback(() => {
    window.open(currentUrl, '_blank', 'noopener,noreferrer');
  }, [currentUrl]);

  const copyUrl = useCallback(() => {
    navigator.clipboard?.writeText(currentUrl).catch(() => {
      // clipboard may be unavailable in non-secure contexts; ignore
    });
  }, [currentUrl]);

  useLayoutEffect(() => {
    return () => {
      screenCapture.current?.setRegionOverride(null);
      screenCaptureAbortController.current.abort();
    };
  }, []);

  const compareVersions = useTypedSelector(
    ({ modal }) => !!modal.compareVersionsModal?.visible
  );
  useEffect(() => {
    const iframe = iframeRef.current;
    const isActive = viewerType !== 'main' || !compareVersions;
    if (!iframe || !grant.ready || error || !isActive) return;

    const abortController = new AbortController();
    screenCaptureAbortController.current = abortController;
    const controller = startScreenCapture(iframe, abortController.signal);
    screenCapture.current = controller;
    controller.setRegionOverride(captureRegionOverrideRef.current);
    return () => {
      controller.setRegionOverride(null);
      abortController.abort();
      if (screenCapture.current === controller) {
        screenCapture.current = null;
      }
    };
  }, [compareVersions, error, grant.ready, startScreenCapture, viewerType]);

  const _cancelAnnotations = () => {
    dismissAnnotation();
    fabricContext.clearCanvas();
  };
  const cancelAnnotations = useRef(_cancelAnnotations);
  cancelAnnotations.current = _cancelAnnotations;

  const wasWritingCommentRef = useRef(isNewComment);
  useLayoutEffect(() => {
    const wasWritingComment = wasWritingCommentRef.current;
    wasWritingCommentRef.current = isNewComment;
    if (wasWritingComment && !isNewComment) {
      clearCanvas();
    }
  }, [clearCanvas, isNewComment]);

  const applyViewport = useCallback((viewport: Viewport) => {
    _setViewport(viewport);
    cancelAnnotations.current();
  }, []);

  const setViewport = useCallback(
    (nextViewport: Viewport) => {
      if (!viewportAvailability[nextViewport]) return;
      applyViewport(nextViewport);
    },
    [applyViewport, viewportAvailability]
  );

  useLayoutEffect(() => {
    if (viewportAvailability[viewport]) return;
    applyViewport(viewportAvailability.tablet ? 'tablet' : 'mobile');
  }, [applyViewport, viewport, viewportAvailability]);

  const pauseAnimations = !!snapshot || shapesDrawingTools.shape !== 'cursor';
  useEffect(() => {
    const bridge = bridgeRef.current;
    if (!bridge) return;
    if (!pauseAnimations) return;
    bridge.send({ type: 'pause-animations' });
    return () => {
      bridge.send({ type: 'resume-animations' });
    };
  }, [pauseAnimations]);

  useLayoutEffect(() => {
    if (snapshotRequested || takingSnapshot) return;
    if (!selectedCommentSnapshotUrl) {
      setAttachmentSnapshot(null);
      return;
    }

    let cancelled = false;
    const image = new Image();
    setLoadingAttachmentSnapshotUrl(selectedCommentSnapshotUrl);
    setAttachmentSnapshot(null);
    image.onload = () => {
      if (cancelled) return;
      setAttachmentSnapshot({
        kind: 'attachment-url',
        url: selectedCommentSnapshotUrl,
        coord: {
          width: image.naturalWidth,
          height: image.naturalHeight
        }
      });
      setLoadingAttachmentSnapshotUrl((loadingUrl) =>
        loadingUrl === selectedCommentSnapshotUrl ? null : loadingUrl
      );
    };
    image.onerror = () => {
      if (cancelled) return;
      setAttachmentSnapshot(null);
      setLoadingAttachmentSnapshotUrl((loadingUrl) =>
        loadingUrl === selectedCommentSnapshotUrl ? null : loadingUrl
      );
    };
    image.src = selectedCommentSnapshotUrl;
    return () => {
      cancelled = true;
    };
  }, [selectedCommentSnapshotUrl, snapshotRequested, takingSnapshot]);

  const prevSnapshotRequested = useRef(snapshotRequested);
  const uppyRef = useRef(uppy);
  uppyRef.current = uppy;

  const currentUrlRef = useRef(currentUrl);
  currentUrlRef.current = currentUrl;

  const canvasRef = useRef(fabricContext.canvas);
  canvasRef.current = fabricContext.canvas;
  const snapshotTransitionIdRef = useRef(0);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || takingSnapshot) return;
    (canvas as any)[WEBSITE_COORDINATE_TRANSITION_FLAG] = false;
  }, [takingSnapshot, snapshot]);

  useLayoutEffect(() => {
    if (prevSnapshotRequested.current === snapshotRequested) {
      return;
    }
    prevSnapshotRequested.current = snapshotRequested;
    if (!snapshotRequested) {
      const snapshotFiles = uppyRef.current.getFiles().filter((file) => {
        return file.name === WEBSITE_SNAPSHOT_ATTACHMENT_NAME;
      });
      uppyRef.current.removeFiles(snapshotFiles.map((file) => file.id));
      return;
    }

    if (!isCurrentTabScreenCaptureSupported()) {
      setIsCaptureUnsupportedModalOpen(true);
      cancelAnnotations.current();
      return;
    }

    const iframe = iframeRef.current;
    if (!iframe) return;

    const transitionId = ++snapshotTransitionIdRef.current;
    setTakingSnapshot(true);

    const canvas = canvasRef.current;
    if (!canvas) {
      setTakingSnapshot(false);
      cancelAnnotations.current();
      return;
    }
    (canvas as any)[WEBSITE_COORDINATE_TRANSITION_FLAG] = true;
    canvas.discardActiveObject();
    canvas.requestRenderAll();

    let controller = screenCapture.current;
    if (!controller) {
      const abortController = new AbortController();
      screenCaptureAbortController.current = abortController;
      screenCaptureSnapshotCompleted.current = false;
      const { signal } = abortController;
      signal.addEventListener('abort', () => {
        screenCapture.current = null;
        if (!screenCaptureSnapshotCompleted.current) {
          cancelAnnotations.current();
        }
      });
      controller = startScreenCapture(iframe, signal);
      screenCapture.current = controller;
    }

    const abortController = new AbortController();
    const takeSnapshot = async () => {
      try {
        screenCaptureSnapshotCompleted.current = false;
        const { blob, width, height, sourceWidth, sourceHeight } =
          await controller.snapshot(abortController.signal);
        abortController.signal.throwIfAborted();
        if (!width || !height || !sourceWidth || !sourceHeight) {
          throw new Error('Captured snapshot has invalid dimensions');
        }
        screenCaptureSnapshotCompleted.current = true;

        const currentCanvas = nonNull(canvasRef.current);
        const scaleX = width / sourceWidth;
        const scaleY = height / sourceHeight;
        currentCanvas.discardActiveObject();
        currentCanvas.getObjects().forEach((object) => {
          (fabric.util as any).addTransformToObject(object, [
            scaleX,
            0,
            0,
            scaleY,
            0,
            0
          ]);
          object.setCoords();
        });
        currentCanvas.requestRenderAll();
        currentCanvas.fire(WEBSITE_HISTORY_REBASE_EVENT);

        setWebsitePageUrl(currentUrlRef.current);
        const existingFiles = uppyRef.current.getFiles().filter((file) => {
          return file.name === WEBSITE_SNAPSHOT_ATTACHMENT_NAME;
        });
        uppyRef.current.removeFiles(existingFiles.map((file) => file.id));
        uppyRef.current.addFile({
          name: WEBSITE_SNAPSHOT_ATTACHMENT_NAME,
          type: 'image/png',
          data: blob,
          meta: {
            websiteSnapshotWidth: width,
            websiteSnapshotHeight: height
          }
        });
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        message.error({
          content: 'Something went wrong while taking a website snapshot.',
          className: 'message-dark-modal'
        });
        cancelAnnotations.current();
      } finally {
        resetInteraction();
        if (snapshotTransitionIdRef.current === transitionId) {
          setTakingSnapshot(false);
        }
      }
    };
    takeSnapshot();
    return () => {
      abortController.abort();
      const currentCanvas = canvasRef.current;
      if (currentCanvas && snapshotTransitionIdRef.current === transitionId) {
        (currentCanvas as any)[WEBSITE_COORDINATE_TRANSITION_FLAG] = false;
      }
    };
  }, [
    isNewComment,
    resetInteraction,
    snapshotRequested,
    startScreenCapture,
    setWebsitePageUrl
  ]);

  const frameElement = (
    <div
      ref={frameRef}
      className={`media_viewer_website__container media_viewer_website__container--${viewport}${
        isMdWidth ? ' media_viewer_website__container--compact-host' : ''
      }${
        isPersistedSnapshot ? ' media_viewer_website__container--snapshot' : ''
      }`}
    >
      {grant.ready && !error ? (
        <div className="media_viewer_website__iframe_mount" ref={mountRef}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              contain: 'paint',
              background: snapshot ? '#1a1c1e' : 'transparent',
              pointerEvents: isCanvasInteractive ? 'auto' : 'none'
            }}
          >
            {snapshot && (
              <img
                src={snapshot.url}
                alt="Snapshot of the website"
                className="website-snapshot"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
                onLoad={syncCanvasSize.current}
              />
            )}
            <div
              ref={canvasOverlayRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                visibility:
                  selectedCommentSnapshotUrl !== null &&
                  loadingAttachmentSnapshotUrl === selectedCommentSnapshotUrl
                    ? 'hidden'
                    : 'visible',
                zIndex: 2
              }}
            >
              <FabricCanvas />
            </div>
          </div>
        </div>
      ) : (
        <PlaybackPreparingLoader
          error={!!grant.error || !!error}
          message={
            grant.error
              ? `Could not authorise the preview: ${grant.error}`
              : error || 'Authorising preview...'
          }
        />
      )}
    </div>
  );

  return {
    frameElement,
    faviconUrl,
    clearFavicon: () => setFaviconUrl(null),
    currentUrl: websitePageUrl,
    viewport,
    viewportAvailability,
    setViewport,
    canBack,
    canForward,
    isLoading,
    loadingButton,
    back,
    forward,
    reload,
    hardReload,
    openInNewTab,
    copyUrl,
    snapshot: !!snapshot,
    takingSnapshot,
    cancelSnapshot: () => {
      cancelAnnotations.current();
    },
    isCaptureUnsupportedModalOpen,
    dismissCaptureUnsupportedModal: () => {
      setIsCaptureUnsupportedModalOpen(false);
    }
  };
}
